import axios from 'axios';
import apiClient from '../api/apiClient.js';
import {
  BBT_CONTRACT_ADDRESS,
  BBT_BNB_LP_ADDRESS,
  BBT_SYMBOL,
  BBT_DECIMALS,
  BBT_CONTRACT_NETWORK_NAME,
  BSC_SCAN_URL,
  PANCAKESWAP_SWAP_URL,
  DEXTOOLS_URL,
  METAMASK_ASSET_INFO
} from '../config/bbt.js';

// Cache pour les données BBT (durée: 30 secondes)
const CACHE_DURATION = 30 * 1000;
let cachedStats = null;
let cacheTimestamp = 0;

class BBTService {
  /**
   * Récupère les informations complètes du token BBT avec fallback
   */
  async getBBTInfo() {
    return this._getDefaultInfo();
  }

  /**
   * Récupère les statistiques en temps réel du token BBT via DexScreener API
   * DexScreener est gratuit et ne nécessite pas de clé API
   */
  async getBBTStats() {
    const now = Date.now();
    
    // Utiliser le cache si les données sont récentes
    if (cachedStats && (now - cacheTimestamp) < CACHE_DURATION) {
      return { data: cachedStats };
    }

    try {
      // DexScreener API - gratuite et sans clé API
      const response = await axios.get(
        `https://api.dexscreener.com/latest/dex/pairs/bsc/${BBT_BNB_LP_ADDRESS}`,
        { timeout: 10000 }
      );

      if (response.data && response.data.pair) {
        const pair = response.data.pair;
        
        const stats = {
          price: parseFloat(pair.priceUsd) || 0,
          priceChange24h: parseFloat(pair.priceChange?.h24) || 0,
          marketCap: parseFloat(pair.fdv) || 0, // Fully Diluted Valuation
          volume24h: parseFloat(pair.volume?.h24) || 0,
          totalSupply: '500000000',
          circulatingSupply: parseFloat(pair.fdv) / (parseFloat(pair.priceUsd) || 1),
          holders: await this._getHoldersCount(), // Récupérer depuis BscScan
          liquidity: parseFloat(pair.liquidity?.usd) || 0,
          isVerified: true,
          dexListings: [
            { 
              name: pair.dexId || 'PancakeSwap', 
              url: PANCAKESWAP_SWAP_URL,
              pairAddress: pair.pairAddress
            }
          ],
          // Données supplémentaires de DexScreener
          priceChange1h: parseFloat(pair.priceChange?.h1) || 0,
          priceChange6h: parseFloat(pair.priceChange?.h6) || 0,
          txns24h: {
            buys: pair.txns?.h24?.buys || 0,
            sells: pair.txns?.h24?.sells || 0
          },
          pairCreatedAt: pair.pairCreatedAt
        };

        // Mettre en cache
        cachedStats = stats;
        cacheTimestamp = now;

        return { data: stats };
      }
    } catch (error) {
      console.warn('[BBTService] DexScreener API error, trying fallback:', error.message);
    }

    // Fallback: GeckoTerminal API (aussi gratuit)
    try {
      const geckoResponse = await axios.get(
        `https://api.geckoterminal.com/api/v2/networks/bsc/pools/${BBT_BNB_LP_ADDRESS}`,
        { 
          timeout: 10000,
          headers: { 'Accept': 'application/json' }
        }
      );

      if (geckoResponse.data?.data?.attributes) {
        const attrs = geckoResponse.data.data.attributes;
        
        const stats = {
          price: parseFloat(attrs.base_token_price_usd) || 0,
          priceChange24h: parseFloat(attrs.price_change_percentage?.h24) || 0,
          marketCap: parseFloat(attrs.fdv_usd) || 0,
          volume24h: parseFloat(attrs.volume_usd?.h24) || 0,
          totalSupply: '500000000',
          circulatingSupply: parseFloat(attrs.reserve_in_usd) * 2 / (parseFloat(attrs.base_token_price_usd) || 1),
          holders: await this._getHoldersCount(),
          liquidity: parseFloat(attrs.reserve_in_usd) || 0,
          isVerified: true,
          dexListings: [{ name: 'PancakeSwap', url: PANCAKESWAP_SWAP_URL }]
        };

        // Mettre en cache
        cachedStats = stats;
        cacheTimestamp = now;

        return { data: stats };
      }
    } catch (geckoError) {
      console.warn('[BBTService] GeckoTerminal API error:', geckoError.message);
    }

    // Fallback final: données par défaut
    return this._getDefaultStats();
  }

  /**
   * Récupère le nombre de holders depuis BscScan (approximatif)
   */
  async _getHoldersCount() {
    try {
      // Note: BscScan API nécessite une clé API pour cette fonctionnalité
      // Pour l'instant, retournons une valeur estimée
      // Vous pouvez ajouter votre clé BscScan API ici
      const bscApiKey = import.meta.env.VITE_BSCSCAN_API_KEY;
      
      if (bscApiKey) {
        const response = await axios.get(
          `https://api.bscscan.com/api?module=token&action=tokenholdercount&contractaddress=${BBT_CONTRACT_ADDRESS}&apikey=${bscApiKey}`,
          { timeout: 5000 }
        );
        
        if (response.data?.status === '1') {
          return parseInt(response.data.result) || 0;
        }
      }
    } catch (error) {
      console.warn('[BBTService] BscScan holders count error:', error.message);
    }
    
    // Valeur par défaut si l'API échoue
    return 0;
  }

  /**
   * Récupère l'historique des prix BBT via DexScreener
   */
  async getBBTPriceHistory(period = '7d') {
    try {
      // DexScreener fournit les données OHLCV
      const response = await axios.get(
        `https://api.dexscreener.com/latest/dex/pairs/bsc/${BBT_BNB_LP_ADDRESS}`,
        { timeout: 10000 }
      );

      if (response.data?.pair) {
        const pair = response.data.pair;
        return {
          data: [],
          period,
          currentPrice: parseFloat(pair.priceUsd) || 0,
          priceChange: {
            h1: parseFloat(pair.priceChange?.h1) || 0,
            h6: parseFloat(pair.priceChange?.h6) || 0,
            h24: parseFloat(pair.priceChange?.h24) || 0
          }
        };
      }
    } catch (error) {
      console.warn('[BBTService] Price history error:', error.message);
    }

    return this._getDefaultPriceHistory(period);
  }

  /**
   * Récupère les métriques de trading BBT
   */
  async getBBTTradingMetrics() {
    try {
      const stats = await this.getBBTStats();
      return {
        volume24h: stats.data.volume24h,
        marketCap: stats.data.marketCap,
        holders: stats.data.holders,
        liquidity: stats.data.liquidity,
        txns24h: stats.data.txns24h || { buys: 0, sells: 0 }
      };
    } catch (error) {
      console.warn('[BBTService] Trading metrics error:', error.message);
      return this._getDefaultMetrics();
    }
  }

  // Méthodes privées pour les données par défaut
  _getDefaultInfo() {
    return {
      data: {
        contract: {
          address: BBT_CONTRACT_ADDRESS,
          name: 'Baobab Token',
          symbol: BBT_SYMBOL,
          decimals: BBT_DECIMALS,
          network: BBT_CONTRACT_NETWORK_NAME,
          verified: true
        }
      }
    };
  }

  _getDefaultStats() {
    return {
      data: {
        price: 0,
        priceChange24h: 0,
        marketCap: 0,
        volume24h: 0,
        totalSupply: '500000000',
        circulatingSupply: 0,
        holders: 0,
        liquidity: 0,
        isVerified: true,
        dexListings: [{ name: 'PancakeSwap', url: PANCAKESWAP_SWAP_URL }]
      }
    };
  }

  _getDefaultPriceHistory(period) {
    return { data: [], period, currentPrice: 0 };
  }

  _getDefaultMetrics() {
    return { volume24h: 0, marketCap: 0, holders: 0, liquidity: 0 };
  }

  /**
   * Vérifie les avantages BBT pour un utilisateur
   */
  async getUserBenefits(userAddress) {
    try {
      const response = await apiClient.get(`/bbt/benefits/${userAddress}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la vérification des avantages BBT:', error);
      throw error;
    }
  }

  /**
   * Génère les liens utiles pour BBT
   */
  generateLinks() {
    return {
      bscscan: BSC_SCAN_URL,
      pancakeswap: PANCAKESWAP_SWAP_URL,
      dextools: DEXTOOLS_URL,
      addToMetaMask: METAMASK_ASSET_INFO
    };
  }

  /**
   * Calcule les avantages basés sur le solde BBT
   */
  calculateBenefits(balance) {
    const numBalance = parseFloat(balance) || 0;
    
    let tier = 'NONE';
    let feeReduction = 0;
    let affiliateMultiplier = 1;
    let priorityAccess = false;
    let exclusiveAirdrops = false;

    if (numBalance >= 100000) { // PLATINUM
      tier = 'PLATINUM';
      feeReduction = 75;
      affiliateMultiplier = 3;
      priorityAccess = true;
      exclusiveAirdrops = true;
    } else if (numBalance >= 50000) { // GOLD
      tier = 'GOLD';
      feeReduction = 60;
      affiliateMultiplier = 2.5;
      priorityAccess = true;
      exclusiveAirdrops = true;
    } else if (numBalance >= 10000) { // SILVER
      tier = 'SILVER';
      feeReduction = 50;
      affiliateMultiplier = 2;
      priorityAccess = true;
      exclusiveAirdrops = true;
    } else if (numBalance >= 1000) { // BRONZE
      tier = 'BRONZE';
      feeReduction = 25;
      affiliateMultiplier = 1.5;
      priorityAccess = false;
      exclusiveAirdrops = true;
    }

    return {
      tier,
      feeReduction,
      affiliateMultiplier,
      priorityAccess,
      exclusiveAirdrops,
      hasBBT: numBalance > 0
    };
  }

  /**
   * Formate le prix BBT avec la devise
   */
  formatPrice(price) {
    const numPrice = Number(price) || 0;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 8
    }).format(numPrice);
  }

  /**
   * Formate les grandes quantités de tokens
   */
  formatTokenAmount(amount) {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    if (!num || isNaN(num)) return '0';
    
    if (num >= 1000000000) {
      return `${(num / 1000000000).toFixed(2)}B`;
    } else if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}K`;
    }
    
    return num.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}

export default new BBTService(); 